import {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useContext,
    useMemo,
    useState,
} from "react";
import { useTranslation } from 'react-i18next';
import {
    apiGetDeviceEventsLogs, apiGetHisLogs, apiGetUserEventsLogs,
} from "../api/activity/activity";
import { AlertState } from "../components/alert/AlertsWrapper";
import { AlertType, AlertVariant } from "../components/alert/Alert";
import { handleApiError } from '../lib/errorHandler';
type TProps = {
    children: ReactNode;
};

type TContext = {
    limit: number;
    offset: number;
    activityList: TActivityList;
    alertMsgs: AlertState | null;
    setAlertMessages: Dispatch<SetStateAction<AlertState | null>>;
    getActivityList: (options: { tabId: string }) => Promise<void>;
    setOffset: Dispatch<SetStateAction<number>>;
    setLimit: Dispatch<SetStateAction<number>>;
    activityLoading: boolean;
};

export type TActivityHis = {
    id: number,
    succeeded: boolean,
    created_stamp: string,
    created_counter: number,
    updated_counter: number,
    skipped_counter: number,
    total_counter: number,
    response_status: number,
    payload: {
        HospitalPharmacyStockId: null
        Status: 1
        page: 1
        pageSize: 50
    },
    exception: string,
    traceback: string,
    user?: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
    };
};

export interface TActivityDeviceLog {
  id: number;
  actor: string;
  created_at: string;
  device: string;
  event_type: string;
}

export interface TActivityUserActionLog {
  id: number;
  action: string;
  action_key: string;
  created_stamp: string;  // timestamp as string
  module_key: string;
  user?: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
    };
}

export type TActivityUserEvent = {
    id: number,
    user: {
        id: number,
        first_name: string,
        last_name: string,
        email: string
    },
    action: string,
    module_key: string,
    action_key: string,
    validated_data: {
        language: string
    },
    created_stamp: Date
};

export type TActivity = TActivityHis | TActivityUserEvent | TActivityDeviceLog;

export type TActivityList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: TActivity[];
  last_updated_stamp?: Date;
};

const ActivityContext = createContext<TContext>({} as TContext);

export const ActivityProvider = ({ children }: TProps) => {
    const [activityList, setActivityList] = useState<TActivityList>({
        count: 0,
        results: [],
    });
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [alertMsgs, setAlertMessages] = useState<AlertState | null>(null);
    const [activityLoading, setActivityLoading] = useState(false);
    const { t } = useTranslation();

    const activityApiMap: Record<string, (params: { limit: number; offset: number }) => Promise<{ data: TActivityList }>> = {
        "his-logs": apiGetHisLogs,
        "device-logs": apiGetDeviceEventsLogs,
        "menu-item": apiGetUserEventsLogs,
    };


    const getActivityList = async (options: { tabId: string }) => {
        setActivityLoading(true);
        const tabId = options.tabId
        const apiFn = activityApiMap[tabId];

        try {
            if (!apiFn) throw new Error("Нет обработчика для выбранной вкладки");

            const response = await apiFn({ limit, offset });

            if (response.data) {
                setActivityList(response.data);
            }
        } catch (error: any) {
            handleError(error, t('errors.activity.loadingLogs'));
        } finally {
            setActivityLoading(false);
        }
    };

    const handleError = (error: any, fallback: string) => {
        const serverError = handleApiError(error, fallback, "activity");
        setAlertMessages({
            type: AlertType.error,
            messages: serverError,
            variant: AlertVariant.Swimming,
        });
    };

    const contextValue = useMemo(
        () => ({
            activityList,
            limit,
            offset,
            alertMsgs,
            setAlertMessages,
            getActivityList,
            setOffset,
            setLimit,
            activityLoading,
        }),
        [
            activityList,
            limit,
            offset,
            alertMsgs,
            activityLoading,
        ]
    );

    return (
        <ActivityContext.Provider value={contextValue}>
            {children}
        </ActivityContext.Provider>
    );
};

export const useActivity = () => useContext(ActivityContext);
